import React from 'react'

// External components
import { Flex, Text, TimeAgo } from '@sketch/components'
import EditorLayout from '../../layouts/EditorLayout'
import DocumentItemsLayout from 'modules/shares/components/DocumentItemsLayout'
import { DocumentItemDropdown } from 'modules/shares/components/DocumentItemDropdown'
import { Header } from '../DesignSystem/Header'
import { ErrorHandler } from '@sketch/tracing'
import EmptyState from 'modules/shares/components/EmptyState'
import { DsmTextEditor } from '../../components'
import LoadingPage from 'components/LoadingPage'
import { EmbeddedErrorPage } from 'modules/shares/components/EmbeddedErrorPage'

// Styles
import {
  Content,
  WrapperDocs,
  LogoDocs,
  SeparatorDocs as Separator,
} from './DesignSystemWithDocs.styles'

// Utils
import { RouteProps } from '@sketch/constants'
import { DesignSystem as DesignSystemType } from '../../types'

// Hooks
import { useGetDesignSystemSectionsQuery } from '@sketch/gql-types'
import { PrivateWorkspaceRouteExtraProps } from 'modules/workspace/containers'
import { useFlag } from 'containers/flags'

export interface DesignSystemProps
  extends RouteProps<'DESIGN_SYSTEM'>,
    PrivateWorkspaceRouteExtraProps {
  designSystem: DesignSystemType
  designSystemLoading: boolean
  loadMoreHandler: () => Promise<any>
  search: string
}

export const DesignSystemWithDocs: React.FC<DesignSystemProps> = ({
  workspace,
  designSystem,
  designSystemLoading,
  loadMoreHandler,
  search,
}) => {
  const isDesignSystemsEditorOn = useFlag('design-systems-editor')
  // gets the sections and their pages in detail to show on the sidebar
  const { data, loading, error } = useGetDesignSystemSectionsQuery({
    variables: {
      identifier: designSystem.identifier,
    },
    skip: !isDesignSystemsEditorOn,
    fetchPolicy: 'network-only',
  })

  const designSystemLibraries = designSystem?.shares.entries || []
  const totalShareCount = designSystem?.shares.meta.totalCount || 0
  const sections = data?.designSystem?.sections ?? []
  const userIsGuest =
    workspace?.userRole === 'GUEST' || workspace?.userRole === undefined

  if (loading) {
    return <LoadingPage.WorkspaceSidebar />
  }

  if (error || !isDesignSystemsEditorOn) {
    return <EmbeddedErrorPage for="unknown" />
  }

  return (
    <EditorLayout
      designSystem={designSystem as DesignSystemType}
      workspace={workspace}
      sections={sections}
    >
      <WrapperDocs>
        <Header
          headerURL={designSystem.header}
          designSystemId={designSystem.identifier}
          userIsGuest={userIsGuest}
        />
        <Content>
          {designSystem.preview && <LogoDocs src={designSystem.preview} />}
          <Text textStyle="header.primary.I" mr={3} pt={6}>
            {designSystem?.name}
          </Text>
          <Text textStyle="header.primary.g" color="foreground.secondary.C">
            {designSystem?.description || 'No description added.'}
          </Text>
          <Text textStyle="copy.quaternary.standard.C" mt={3}>
            Updated <TimeAgo date={designSystem?.updatedAt} />
          </Text>
          <Separator />
          <DsmTextEditor content={JSON.parse(designSystem?.content || '{}')} />
          <Separator />
          <Text textStyle="header.primary.H" mt={8}>
            Libraries
          </Text>
          <Text textStyle="copy.tertiary.standard.F">
            Libraries contain components (Symbols, Text Styles, Color Variables
            and Layer Styles) that you can easily share across all your other
            documents.
          </Text>
          <Flex flexDirection="column" flex="auto" pt={8}>
            {totalShareCount ? (
              <DocumentItemsLayout
                loading={designSystemLoading}
                shares={designSystemLibraries}
                renderDropdown={({ identifier }) => {
                  const share = designSystemLibraries.find(
                    library => library.identifier === identifier
                  )
                  if (!share) {
                    ErrorHandler.shouldNeverHappen(
                      'Library should always be present rendering the DocumentDropdown'
                    )
                    return null
                  }
                  return (
                    <DocumentItemDropdown
                      share={share}
                      workspaceIdentifier={workspace.identifier}
                    />
                  )
                }}
                onLoadMore={loadMoreHandler}
                totalShareCount={totalShareCount}
                showProjectName={false}
                customColumns={[2, 2, 2, 2, 2, 3, 4, 5]}
              />
            ) : (
              <EmptyState
                search={search}
                title="No Libraries available to view yet"
                description="Libraries shared with you in this Workspace will appear here"
                icon="link"
              />
            )}
          </Flex>
        </Content>
      </WrapperDocs>
    </EditorLayout>
  )
}
